import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Coins, Save, RefreshCw, Power, PowerOff, AlertTriangle, RotateCcw, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Slots Jackpot Config Panel
 *
 * Backs the following endpoints:
 *   GET  /api/admin/casino-jackpot/config
 *   PUT  /api/admin/casino-jackpot/config
 *   GET  /api/admin/casino-jackpot/status  (for live pot/tickets snapshot)
 *   POST /api/admin/casino-jackpot/draw-now
 *   POST /api/admin/casino-jackpot/draw-yesterday
 *
 * Purpose: gives admin control over the daily Slots Jackpot (which skims a
 * % of every Majik topup into a nightly-drawn pot). Silent pause supported —
 * setting enabled=false stops tickets, pot growth, and the midnight draw with
 * ZERO player-visible signal.
 */
export const CasinoJackpotConfigPanel = () => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [drawing, setDrawing] = useState('');
    const [cfg, setCfg] = useState(null);           // server config
    const [edited, setEdited] = useState(null);     // local draft
    const [defaults, setDefaults] = useState(null);
    const [status, setStatus] = useState(null);     // live pot/tickets/draws
    const [savings, setSavings] = useState(null);   // pause-savings tally

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: c }, { data: s }, { data: sv }] = await Promise.all([
                axios.get(`${API}/admin/casino-jackpot/config`, { withCredentials: true }),
                axios.get(`${API}/admin/casino-jackpot/status`, { withCredentials: true }),
                axios.get(`${API}/admin/casino-jackpot/pause-savings`, { withCredentials: true }),
            ]);
            const { defaults: d, ...rest } = c;
            setCfg(rest);
            setEdited(rest);
            setDefaults(d);
            setStatus(s);
            setSavings(sv);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load casino jackpot config');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const isDirty = useMemo(() => {
        if (!cfg || !edited) return false;
        return JSON.stringify(cfg) !== JSON.stringify(edited);
    }, [cfg, edited]);

    const save = async () => {
        if (!edited) return;
        // Only send changed fields
        const patch = {};
        for (const k of Object.keys(edited)) {
            if (edited[k] !== cfg[k]) patch[k] = edited[k];
        }
        if (Object.keys(patch).length === 0) {
            toast.info('No changes to save');
            return;
        }
        setSaving(true);
        try {
            await axios.put(`${API}/admin/casino-jackpot/config`, patch, { withCredentials: true });
            toast.success('Slots Jackpot config saved');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const resetToDefaults = () => {
        if (!defaults) return;
        setEdited({ ...edited, ...defaults });
        toast.info('Draft reset to defaults — click Save to apply');
    };

    const runDraw = async (which) => {
        setDrawing(which);
        try {
            const url = which === 'today'
                ? `${API}/admin/casino-jackpot/draw-now`
                : `${API}/admin/casino-jackpot/draw-yesterday`;
            const { data } = await axios.post(url, {}, { withCredentials: true });
            if (data.already_drawn) {
                toast.info(`Already drawn for ${data.draw?.date_ct || which}`);
            } else if (data.skipped) {
                toast.info(`Draw skipped: ${data.reason || data.draw?.skipped_reason || 'no eligible tickets'}`);
            } else if (data.drawn && data.draw) {
                toast.success(`Winner: ${data.draw.winner_name} · $${data.draw.prize.toFixed(2)}`);
            } else {
                toast.success('Draw complete');
            }
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Draw failed');
        } finally {
            setDrawing('');
        }
    };

    if (!edited || !cfg) {
        return (
            <div className="card-dark p-5 text-[#A0AAB2] text-xs" data-testid="casino-jackpot-cfg-loading">
                Loading Slots Jackpot config…
            </div>
        );
    }

    const enabled = !!edited.enabled;

    // Live-preview: given today's known topup volume, how much of it flows to the pot?
    // Guard against divide-by-zero when the admin has saved pot_pct=0 (tickets-only mode).
    const currentPct = Number(cfg?.pot_pct || 0);
    const topupVolume = currentPct > 0 && status?.raw_pot ? Number(status.raw_pot) / currentPct : 0;
    const wouldBePotAtNewPct = topupVolume * (edited.pot_pct || 0);

    return (
        <div className="card-dark p-5 space-y-4" data-testid="casino-jackpot-config-panel">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Coins className={`w-5 h-5 ${enabled ? 'text-[#FFD700]' : 'text-[#A0AAB2]'}`} />
                    <h3 className="text-sm uppercase font-bold text-white tracking-wide">
                        Daily Slots Jackpot Config
                    </h3>
                    <span
                        className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${
                            enabled
                                ? 'bg-[#39FF14]/15 text-[#39FF14] border-[#39FF14]/40'
                                : 'bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40'
                        }`}
                        data-testid="cj-status-badge"
                    >
                        {enabled ? 'RUNNING' : 'PAUSED (silent)'}
                    </span>
                </div>
                <div className="flex gap-2">
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={load}
                        disabled={loading}
                        className="text-[#A0AAB2] h-8 text-xs"
                        data-testid="cj-refresh-btn"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Master ON/OFF row (silent pause) */}
            <div
                className="flex items-center justify-between p-4 rounded-lg border gap-4"
                style={{
                    backgroundColor: enabled ? 'rgba(255,215,0,0.06)' : 'rgba(255,59,48,0.06)',
                    borderColor: enabled ? 'rgba(255,215,0,0.4)' : 'rgba(255,59,48,0.35)',
                }}
                data-testid="cj-master-toggle-row"
            >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    {enabled
                        ? <Power className="w-5 h-5 shrink-0 text-[#39FF14]" />
                        : <PowerOff className="w-5 h-5 shrink-0 text-[#FF3B30]" />}
                    <div className="min-w-0">
                        <p className={`text-sm font-bold ${enabled ? 'text-[#FFD700]' : 'text-[#FF3B30]'}`}>
                            Master Switch — {enabled ? 'Slots Jackpot is LIVE' : 'Slots Jackpot is SILENTLY PAUSED'}
                        </p>
                        <p className="text-[11px] text-[#A0AAB2] mt-0.5">
                            {enabled
                                ? `Each Majik topup awards ${cfg.tickets_per_dollar} ticket(s) per $1 and adds ${(cfg.pot_pct * 100).toFixed(2)}% to today's pot. Midnight CT draws one winner.`
                                : 'No tickets awarded, no pot growth, no draw. Players see zero change to their topup UI.'}
                        </p>
                    </div>
                </div>
                <Switch
                    checked={enabled}
                    onCheckedChange={(v) => setEdited({ ...edited, enabled: v })}
                    data-testid="cj-master-toggle"
                />
            </div>

            {/* Pause warning banner */}
            {!enabled && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-[#FF3B30]/10 border border-[#FF3B30]/30" data-testid="cj-paused-banner">
                    <AlertTriangle className="w-4 h-4 text-[#FF3B30] shrink-0 mt-0.5" />
                    <div className="text-[11px] text-[#FF3B30]">
                        <span className="font-bold">Silent pause active.</span> Player-facing Slots Jackpot cards still render off cached data unless you also hide them.
                        Any pending draws will be skipped until you flip this switch back on.
                    </div>
                </div>
            )}

            {/* Since-paused savings counter — only when paused + we have a paused_at */}
            {!enabled && savings?.paused_at && (
                <div
                    className="p-4 rounded-lg border flex flex-wrap items-center justify-between gap-3"
                    style={{ backgroundColor: 'rgba(57,255,20,0.06)', borderColor: 'rgba(57,255,20,0.35)' }}
                    data-testid="cj-savings-block"
                >
                    <div>
                        <p className="text-[10px] uppercase text-[#39FF14] tracking-widest font-bold">
                            Since paused, saved
                        </p>
                        <p className="text-3xl font-mono font-black text-[#39FF14] mt-0.5" data-testid="cj-savings-amount">
                            ${(savings.saved_amount || 0).toFixed(2)}
                        </p>
                        <p className="text-[10px] text-[#A0AAB2] mt-1">
                            {savings.topups_count} topup{savings.topups_count === 1 ? '' : 's'} · ${(savings.topups_since_pause || 0).toFixed(2)} volume · @ {((savings.paused_pot_pct || 0) * 100).toFixed(2)}% skip-rate
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="text-[10px] uppercase text-[#A0AAB2] tracking-wider font-bold">Paused since</p>
                        <p className="text-xs font-mono text-white mt-0.5" data-testid="cj-paused-since">
                            {new Date(savings.paused_at).toLocaleString('en-US', {
                                timeZone: 'America/Chicago',
                                month: 'short', day: 'numeric',
                                hour: 'numeric', minute: '2-digit',
                            })} CT
                        </p>
                        <p className="text-[10px] text-[#A0AAB2] mt-1">
                            {(() => {
                                const ms = Date.now() - new Date(savings.paused_at).getTime();
                                const hrs = ms / 3_600_000;
                                if (hrs < 1) return `${Math.max(1, Math.round(ms / 60_000))} min ago`;
                                if (hrs < 48) return `${hrs.toFixed(1)} hrs ago`;
                                return `${(hrs / 24).toFixed(1)} days ago`;
                            })()}
                        </p>
                    </div>
                </div>
            )}

            {/* Live status row */}
            {status && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="cj-live-status">
                    <div className="p-3 rounded-lg bg-[#0a0a0a] border border-white/5">
                        <p className="text-[10px] uppercase text-[#A0AAB2] tracking-wider">Today&apos;s raw pot ({status.date_ct} CT)</p>
                        <p className="text-2xl font-mono font-black text-[#FFD700]">${status.raw_pot?.toFixed(2)}</p>
                        <p className="text-[10px] text-[#A0AAB2] mt-1">
                            Display floor: ${status.min_pot?.toFixed(2)}
                        </p>
                    </div>
                    <div className="p-3 rounded-lg bg-[#0a0a0a] border border-white/5">
                        <p className="text-[10px] uppercase text-[#A0AAB2] tracking-wider">Tickets today (Top 10 shown)</p>
                        <p className="text-2xl font-mono font-black text-[#39FF14]">{status.total_tickets_today || 0}</p>
                        <p className="text-[10px] text-[#A0AAB2] mt-1">
                            {status.leaderboard?.length || 0} player(s) on today&apos;s board
                        </p>
                    </div>
                    <div className="p-3 rounded-lg bg-[#0a0a0a] border border-white/5">
                        <p className="text-[10px] uppercase text-[#A0AAB2] tracking-wider">Next draw</p>
                        <p className="text-sm font-mono font-bold text-white">
                            {status.next_draw_utc ? new Date(status.next_draw_utc).toLocaleString('en-US', {
                                timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                            }) + ' CT' : '—'}
                        </p>
                        <p className="text-[10px] text-[#A0AAB2] mt-1">
                            {enabled ? 'Will run automatically' : 'Will NOT run while paused'}
                        </p>
                    </div>
                </div>
            )}

            {/* Tunables */}
            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${enabled ? '' : 'opacity-60'}`}>
                <div>
                    <Label className="text-white text-xs">Pot % of each topup</Label>
                    <div className="flex items-center gap-2">
                        <Input
                            type="number" step="0.01" min="0" max="1"
                            value={edited.pot_pct}
                            onChange={e => setEdited({ ...edited, pot_pct: Math.max(0, Math.min(1, parseFloat(e.target.value) || 0)) })}
                            className="mt-1 bg-[#0a0a0a] border-white/10 text-white font-mono"
                            data-testid="cj-cfg-pot-pct"
                        />
                        <span className="text-[#A0AAB2] text-xs pt-1 whitespace-nowrap">
                            = {((edited.pot_pct || 0) * 100).toFixed(2)}%
                        </span>
                    </div>
                    <p className="text-[10px] text-[#A0AAB2] mt-1">
                        Slice of every Majik topup routed to today&apos;s pot. Default: {((defaults?.pot_pct || 0) * 100).toFixed(2)}%.
                        Set to 0 to stop pot growth without pausing tickets.
                    </p>
                </div>

                <div>
                    <Label className="text-white text-xs">Tickets per $1 topped up</Label>
                    <Input
                        type="number" step="0.1" min="0" max="100"
                        value={edited.tickets_per_dollar}
                        onChange={e => setEdited({ ...edited, tickets_per_dollar: Math.max(0, parseFloat(e.target.value) || 0) })}
                        className="mt-1 bg-[#0a0a0a] border-white/10 text-white font-mono"
                        data-testid="cj-cfg-tickets"
                    />
                    <p className="text-[10px] text-[#A0AAB2] mt-1">
                        Draw weight per $1 topup. Default: {defaults?.tickets_per_dollar}. Fractions allowed.
                    </p>
                </div>

                <div>
                    <Label className="text-white text-xs">Minimum pot (house floor)</Label>
                    <Input
                        type="number" step="1" min="0"
                        value={edited.min_pot}
                        onChange={e => setEdited({ ...edited, min_pot: Math.max(0, parseFloat(e.target.value) || 0) })}
                        className="mt-1 bg-[#0a0a0a] border-white/10 text-white font-mono"
                        data-testid="cj-cfg-min-pot"
                    />
                    <p className="text-[10px] text-[#A0AAB2] mt-1">
                        House seeds the difference if raw pot &lt; this on draw. Default: ${defaults?.min_pot?.toFixed(2)}.
                    </p>
                </div>

                <div>
                    <Label className="text-white text-xs">Prize rollover multiplier</Label>
                    <Input
                        type="number" step="0.5" min="0" max="100"
                        value={edited.prize_rollover}
                        onChange={e => setEdited({ ...edited, prize_rollover: Math.max(0, parseFloat(e.target.value) || 0) })}
                        className="mt-1 bg-[#0a0a0a] border-white/10 text-white font-mono"
                        data-testid="cj-cfg-rollover"
                    />
                    <p className="text-[10px] text-[#A0AAB2] mt-1">
                        Wagering requirement on the granted bonus. Default: {defaults?.prize_rollover}×.
                    </p>
                </div>
            </div>

            {/* Live preview of impact */}
            {status && topupVolume > 0 && (
                <div className="p-3 rounded-lg bg-[#FFD700]/5 border border-[#FFD700]/20 text-[11px] text-[#A0AAB2]" data-testid="cj-preview">
                    <span className="text-white font-bold">Live preview:</span> today&apos;s topup volume so far is ~${topupVolume.toFixed(2)}.
                    With current pot % of {((cfg.pot_pct || 0) * 100).toFixed(2)}% the pot is ${status.raw_pot?.toFixed(2)}.
                    {' '}Change {' '}
                    {edited.pot_pct !== cfg.pot_pct && (
                        <>
                            to <span className="text-[#FFD700] font-mono font-bold">{((edited.pot_pct || 0) * 100).toFixed(2)}%</span> and today&apos;s pot would be ~<span className="text-[#FFD700] font-mono font-bold">${wouldBePotAtNewPct.toFixed(2)}</span>{' '}
                            (
                            {wouldBePotAtNewPct >= status.raw_pot ? '+' : '−'}
                            ${Math.abs(wouldBePotAtNewPct - status.raw_pot).toFixed(2)}
                            ).
                        </>
                    )}
                    {edited.pot_pct === cfg.pot_pct && (
                        <>the pot % if you want to see a projection.</>
                    )}
                </div>
            )}

            {/* Actions row */}
            <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button
                    onClick={save}
                    disabled={saving || !isDirty}
                    className="bg-[#FFD700] text-black hover:bg-[#FFD700]/90 font-bold"
                    data-testid="cj-save-btn"
                >
                    <Save className="w-4 h-4 mr-1" />
                    {saving ? 'Saving…' : isDirty ? 'Save Config' : 'Saved'}
                </Button>
                <Button
                    variant="outline"
                    onClick={resetToDefaults}
                    disabled={saving}
                    className="border-white/20 text-white hover:bg-white/5"
                    data-testid="cj-reset-defaults-btn"
                >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Reset draft to defaults
                </Button>
                <div className="flex-1" />
                <Button
                    variant="outline"
                    onClick={() => runDraw('today')}
                    disabled={!!drawing}
                    className="border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10"
                    data-testid="cj-draw-now-btn"
                >
                    <Zap className="w-4 h-4 mr-1" />
                    {drawing === 'today' ? 'Drawing…' : 'Draw now (today)'}
                </Button>
                <Button
                    variant="outline"
                    onClick={() => runDraw('yesterday')}
                    disabled={!!drawing}
                    className="border-white/20 text-white hover:bg-white/5"
                    data-testid="cj-draw-yesterday-btn"
                >
                    {drawing === 'yesterday' ? 'Drawing…' : 'Backfill yesterday'}
                </Button>
            </div>

            {/* Recent draws */}
            {status?.recent_draws?.length > 0 && (
                <div className="mt-3">
                    <p className="text-[10px] uppercase text-[#A0AAB2] tracking-wider font-bold mb-2">Recent draws</p>
                    <div className="space-y-1.5">
                        {status.recent_draws.map((d, i) => (
                            <div
                                key={i}
                                className="flex items-center justify-between p-2 rounded-md bg-[#0a0a0a] border border-white/5 text-[11px]"
                                data-testid={`cj-draw-row-${i}`}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="text-[#A0AAB2] font-mono">{d.date_ct}</span>
                                    {d.skipped_reason ? (
                                        <span className="text-[#FF3B30]">skipped · {d.skipped_reason}</span>
                                    ) : (
                                        <>
                                            <span className="text-white font-semibold">{d.winner_name || '—'}</span>
                                            <span className="text-[#A0AAB2]">· {d.tickets_total} tix · {d.players} players</span>
                                        </>
                                    )}
                                </div>
                                <span className={`font-mono font-bold ${d.skipped_reason ? 'text-[#A0AAB2]' : 'text-[#FFD700]'}`}>
                                    ${(d.prize || 0).toFixed(2)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CasinoJackpotConfigPanel;
